import React from 'react';
import removeMd from 'remove-markdown';

export const textToParagraph = ({ text, className }: { text: string; className?: string }) => {
  //<condition>anxiety</condition>
  //<med>prozac</med>
  const tags = ['condition', 'med', 'treatment', 'test'];
  const tagRegex = new RegExp(`<(?:${tags.join('|')})([^>]*)>(.*?)<\/(?:${tags.join('|')})>`, 'gi');
  const matches = text.match(tagRegex);
  const children = [] as any;

  // If there are no matching tags, just set the text content of the <p> element
  if (!matches) {
    return <p className={className}>{text}</p>;
  }

  let lastIndex = 0;

  // Iterate over the matching tags and their contents
  for (const match of matches) {
    // Get the index of the current match in the original string
    const matchIndex = text.indexOf(match, lastIndex);

    // Get the text before the current match and add it to the <p> element
    const beforeText = text.substring(lastIndex, matchIndex);
    if (beforeText) {
      children.push(beforeText);
    }

    // Create a new <span> element and add it to the <p> element
    const spanContent = match
      .replace(new RegExp(`^<(${tags.join('|')})([^>]*)>`, 'i'), '')
      .replace(new RegExp(`<\/(${tags.join('|')})>$`, 'i'), '');
    const tagMatch = match.match(new RegExp(`^<(${tags.join('|')})`, 'i'));
    const span = <span className={tagMatch ? tagMatch[1] : ''}>{spanContent}</span>;

    children.push(span);

    // Update the lastIndex variable to the end of the current match
    lastIndex = matchIndex + match.length;
  }

  // Get the text after the last matching tag and add it to the <p> element
  const afterText = text.substring(lastIndex);
  if (afterText) {
    children.push(afterText);
  }

  return <p className={className}>{children.map((el: any) => el)}</p>;
};

export const stripTags = (str: string) => {
  const tags = ['test', 'treatment', 'med', 'condition'];
  const regex = new RegExp(`<\\/?(?:${tags.join('|')})>`, 'gi');
  return str.replace(regex, '');
};

type FormatType = 'date' | 'time' | 'both' | 'datelong';

export const formatDate = (epochTime: number, formatType: FormatType = 'both'): string => {
  const date = new Date(epochTime * 1000);

  let options: Intl.DateTimeFormatOptions = {};

  switch (formatType) {
    case 'date':
      options = {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
      };
      break;
    case 'time':
      options = {
        hour: 'numeric',
        minute: 'numeric',
      };
      break;
    case 'datelong':
      options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      };
      break;
    case 'both':
    default:
      options = {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      };
      break;
  }

  const formattedDate = date.toLocaleString('en-US', options);
  return formattedDate;
};

export const truncateText = (element: HTMLElement, maxLines: number) => {
  const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
  const maxHeight = maxLines * lineHeight;
  let text = element.textContent || '';

  while (element.scrollHeight > maxHeight && text.length > 0) {
    text = text.slice(0, -1);
    element.textContent = text + '...';
  }
};

export const formatDuration = (seconds: number): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    // Display hours and minutes
    return `${pad(hours)}:${pad(minutes)} hours`;
  } else if (minutes > 0) {
    // Display only minutes if duration is between 1 minute and 1 hour
    return `${pad(minutes)} minutes`;
  } else {
    // Display only seconds if duration is less than 1 minute
    return `${pad(remainingSeconds)} seconds`;
  }
};

export const unixTimestampToDaysRemaining = (epochTime: number): number => {
  const currentTimestamp = Math.floor(Date.now() / 1000); // Current timestamp in seconds
  const secondsRemaining = epochTime - currentTimestamp;

  if (secondsRemaining <= 0) {
    return 0; // The timestamp has already passed
  }

  const secondsInADay = 60 * 60 * 24;
  const daysRemaining = Math.ceil(secondsRemaining / secondsInADay);

  return daysRemaining;
};

// Step 1: Build a mixed array of strings and JSX elements with formatting applied.
const buildFormattedContent = (diffs: [number, string][]): (string | JSX.Element)[] => {
  return diffs.reduce<(string | JSX.Element)[]>((acc, [operation, text], index) => {
    if (operation === 1) {
      // Addition with bold formatting
      acc.push(
        <span key={`add-${index}`} className="font-bold">
          {text}
        </span>,
      );
    } else if (operation === 0) {
      // Unchanged text
      acc.push(text);
    }
    // Ignore deletion (-1)
    return acc;
  }, []);
};

// Step 2: Process the content for newlines, inserting divs for spacing.
const processContentForNewlines = (content: (string | JSX.Element)[]): JSX.Element[] => {
  let keyCounter = 0; // For unique keys
  const processedContent: JSX.Element[] = [];

  content.forEach((item, index) => {
    if (typeof item === 'string') {
      // For strings, split by '\n' and insert divs for spacing
      item.split('\n').forEach((part, partIndex, array) => {
        if (part) {
          processedContent.push(<span key={`text-${keyCounter++}`}>{part}</span>);
        }
        // Add divs for newline, except after the last segment
        if (partIndex < array.length - 1) {
          processedContent.push(<div key={`space-${keyCounter++}`} className="py-2"></div>);
        }
      });
    } else {
      // JSX Elements (formatted as bold) are added directly
      processedContent.push(item);
    }
  });

  return processedContent;
};

// Integrating the steps in the final render function
export const renderDiffs = (diffs: [number, string][]): JSX.Element => {
  const formattedContent = buildFormattedContent(diffs);
  const processedContent = processContentForNewlines(formattedContent);

  return <>{processedContent}</>;
};

export const compareVersions = (v1: string, v2: string) => {
  const v1Parts = v1.split('.').map(Number);
  const v2Parts = v2.split('.').map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const part1 = v1Parts[i] || 0;
    const part2 = v2Parts[i] || 0;

    if (part1 !== part2) {
      return part1 > part2 ? 1 : -1;
    }
  }

  return 0;
};

export const formatDateString = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// export const renderDiff = (diff: [number, string], index: number) => {
//   // Split the text by newlines to process each line individually
//   const lines = diff[1].split("\n").filter((line) => line);

//   return lines.map((line, i) => {
//     console.log(line);

//     // Conditionally format text based on diff type
//     switch (diff[0]) {
//       case 1: // New text, should be bold
//         return (
//           <React.Fragment key={`${index}-${i}`}>
//             {i > 0 && <div className="py-2" />}
//             {/* Insert newlines between lines, but not before the first line */}
//             <span className="font-bold">{line}</span>
//           </React.Fragment>
//         );
//       case 0: // Original text
//         return (
//           <React.Fragment key={`${index}-${i}`}>
//             {i > 0 && <div className="py-2" />}
//             {/* Insert newlines between lines, but not before the first line */}
//             {/* {textToParagraph({ text: line, className: "py-2" })} */}
//             {line}
//           </React.Fragment>
//         );
//       default:
//         return null;
//     }
//   });
// };

export const truncateTextByCharCount = (text: string, maxChars: number = 240): string => {
  if (text.length <= maxChars) return text;

  // Find the last space before maxChars
  const lastSpace = text.lastIndexOf(' ', maxChars);

  // If no space found, just cut at maxChars
  if (lastSpace === -1) {
    return text.slice(0, maxChars).trim() + '...';
  }

  // Cut at the last space
  return text.slice(0, lastSpace).trim() + '...';
};

export const formatContent = (content: React.ReactNode, fontSize: string | number = '1rem') => {
  if (typeof content === 'string') {
    return content.split('\n').map((text, index) => (
      <p key={index} style={{ margin: 0, paddingTop: 2, paddingBottom: 2, fontSize }}>
        {text}
      </p>
    ));
  }
  return content;
};

export const capitalize = (str: string | undefined): string => {
  if (!str) return str || '';
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Converts a snake_case string to Title Case
 * e.g., "patient_letter" -> "Patient Letter"
 */
export const formatSnakeCase = (str: string | null | undefined): string => {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Normalizes markdown input by converting literal escape sequences
 * and fixing common malformed patterns
 * This should run as the first step in the markdown processing pipeline
 */
export const normalizeMarkdownInput = (text: string): string => {
  if (!text) return '';

  // 1. Convert literal \r\n and \n strings to actual newlines
  let out = text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');

  // 2. Convert Unicode bullet characters to markdown list markers
  // \u2022 (•), \u2023 (‣), \u25E6 (◦), \u2043 (⁃) → "-"
  out = out.replace(/^(\s*)[\u2022\u2023\u25E6\u2043]/gm, '$1-');

  // 3. Fix malformed ordered list patterns: "-1." or "- 1." → "1."
  out = out.replace(/^(\s*)-\s*(\d+)\.\s/gm, '$1$2. ');

  // 4. Fix standalone list markers separated from content by blank lines
  // Pattern: "* \n\n Content" → "* Content"
  // Handles *, -, + markers with optional trailing spaces, followed by blank lines
  out = out.replace(/^(\s*[*\-+])\s*\n+(\s*)(?=[^\s*\-+\n])/gm, '$1 ');

  // 5. Collapse excessive blank lines between list items to form proper lists
  // Pattern: "- Item 1\n\n\n- Item 2" → "- Item 1\n- Item 2"
  // Also handles ordered lists: "1. Item 1\n\n\n2. Item 2" → "1. Item 1\n2. Item 2"
  // This ensures list items are consecutive for proper markdown list rendering
  out = out.replace(/^(\s*(?:[*\-+]|\d+\.)\s+.+)\n{2,}(?=\s*(?:[*\-+]|\d+\.)\s)/gm, '$1\n');

  return out;
};

/**
 * Utility function to normalize markdown to preserve single newlines
 * Converts single newlines (that aren't part of lists, headings, or code blocks) into paragraph breaks
 * This ensures that BlockNote renders line breaks correctly
 */
export const normalizeMarkdownNewlines = (text: string): string => {
  if (!text) return '';

  const lines = text.split('\n');
  const normalized: string[] = [];
  let inCodeBlock = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = i < lines.length - 1 ? lines[i + 1] : null;
    const prevLine = i > 0 ? lines[i - 1] : null;

    // Track code blocks
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      normalized.push(line);
      continue;
    }

    // Preserve code blocks as-is
    if (inCodeBlock) {
      normalized.push(line);
      continue;
    }

    // Preserve empty lines (they're already paragraph breaks)
    if (line.trim() === '') {
      normalized.push(line);
      continue;
    }

    // Preserve headings (lines starting with #)
    if (/^#{1,6}\s/.test(line.trim())) {
      normalized.push(line);
      continue;
    }

    // Preserve list items (lines starting with -, *, +, or numbers)
    if (/^(\s*)([-*+]|\d+\.)\s/.test(line)) {
      normalized.push(line);
      continue;
    }

    // Preserve horizontal rules
    if (/^[-*_]{3,}$/.test(line.trim())) {
      normalized.push(line);
      continue;
    }

    // Preserve blockquotes
    if (/^\s*>/.test(line)) {
      normalized.push(line);
      continue;
    }

    // For regular lines, check if we need to add a paragraph break
    // This converts single newlines between regular text into paragraph breaks
    const isRegularLine = (l: string | null) => {
      if (!l || l.trim() === '') return false;
      return (
        !/^#{1,6}\s/.test(l.trim()) &&
        !/^(\s*)([-*+]|\d+\.)\s/.test(l) &&
        !/^[-*_]{3,}$/.test(l.trim()) &&
        !l.trim().startsWith('```') &&
        !/^\s>/.test(l)
      );
    };

    const prevIsRegular = isRegularLine(prevLine);
    const nextIsRegular = isRegularLine(nextLine);
    const currentIsRegular = isRegularLine(line);

    // If previous line was regular and current is regular, add paragraph break before current line
    // This converts the single newline between them into a paragraph break
    if (prevIsRegular && currentIsRegular) {
      // Check if we already added a break (avoid duplicates)
      const lastNormalized = normalized[normalized.length - 1];
      if (lastNormalized && lastNormalized.trim() !== '') {
        normalized.push('');
      }
    }

    normalized.push(line);
  }

  return normalized.join('\n');
};

/**
 * Utility function to extract markdown content from code blocks
 * If the text contains markdown code blocks (```), it extracts just the content
 * Otherwise returns the original text
 */
export const extractMarkdownFromCodeBlocks = (text: string): string => {
  if (!text) return '';

  // Check if the text contains markdown code blocks
  if (text.includes('```markdown') || text.includes('```')) {
    // Regular expression to match markdown code blocks
    const codeBlockRegex = /```(?:markdown)?\s*([\s\S]*?)```/g;
    const matches = [...text.matchAll(codeBlockRegex)];

    if (matches.length > 0) {
      // Extract content from all code blocks and join them
      return matches.map(match => match[1].trim()).join('\n\n');
    }
  }

  // Return original text if no code blocks found
  return text;
};

export const copyRichText = async (
  html: string,
  plainText: string,
  includeHtml: boolean = true,
) => {
  try {
    // 1. Protect Setext headers by capturing their content in an array
    const headerRegex = /^={3,}\s*([\s\S]*?)\s*={3,}$/gim;
    const placeholders: string[] = [];
    const protectedText = plainText.replace(headerRegex, (_match, inner) => {
      const token = `@@HEADING_${placeholders.length}@@`;
      placeholders.push(inner);
      return token;
    });

    // 2. Strip Markdown as usual
    const stripped = removeMd(protectedText, {
      stripListLeaders: false, // example setting
      useImgAltText: false, // example setting
    });

    // 3. Restore your headers
    const cleanPlainText = stripped.replace(/@@HEADING_(\d+)@@/g, (_m, idx) => {
      const content = placeholders[Number(idx)];
      return `=== ${content} ===`;
    });

    // If HTML formatting is disabled, only copy plain text
    if (!includeHtml) {
      await navigator.clipboard.writeText(cleanPlainText);
      return true;
    }

    if (navigator.clipboard && window.ClipboardItem) {
      const blobHtml = new Blob([html], { type: 'text/html' });
      const blobText = new Blob([cleanPlainText], { type: 'text/plain' });

      const data = [
        new ClipboardItem({
          'text/html': blobHtml,
          'text/plain': blobText,
        }),
      ];

      await navigator.clipboard.write(data);
      return true;
    } else {
      // Fallback for browsers that don't support ClipboardItem
      const tempElement = document.createElement('div');
      tempElement.innerHTML = html;
      tempElement.style.position = 'absolute';
      tempElement.style.left = '-9999px';
      document.body.appendChild(tempElement);

      const selection = window.getSelection();
      if (!selection) {
        throw new Error('Failed to get selection');
      }

      const range = document.createRange();
      range.selectNodeContents(tempElement);
      selection.removeAllRanges();
      selection.addRange(range);

      const successful = document.execCommand('copy');
      selection.removeAllRanges();
      document.body.removeChild(tempElement);

      if (!successful) {
        await navigator.clipboard.writeText(cleanPlainText);
      }
      return true;
    }
  } catch (err) {
    console.error('Copy failed:', err);
    try {
      const cleanPlainText = removeMd(plainText, {
        stripListLeaders: false,
        useImgAltText: false,
      });
      await navigator.clipboard.writeText(cleanPlainText);
      return true;
    } catch (clipboardErr) {
      console.error('Plain text copy also failed:', clipboardErr);
      return false;
    }
  }
};
